import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { openReportPopup } from './popupWindow';

/**
 * Popup-window manager for generated reports. Each generated report opens as a
 * real OS popup (window.open) the user can drag onto a second monitor; the report
 * renders into it via a React portal (see ReportPopup). Mounted once at app root.
 */
const ReportWindowsContext = createContext(null);

let _seq = 0;
const nextId = () => `rw_${Date.now()}_${_seq++}`;

export const ReportWindowsProvider = ({ children }) => {
  const [windows, setWindows] = useState([]);
  // Live popup handles + per-window cancel hooks, kept out of React state (not
  // serialisable; must not drive re-renders).
  const handles = useRef(new Map());
  const cancels = useRef(new Map());

  const statusOf = useCallback((id) => {
    const w = windowsRef.current.find((x) => x.id === id);
    return w ? w.status : null;
  }, []);
  // Mirror windows into a ref so closeReportWindow can read the latest status
  // without being re-created on every windows change.
  const windowsRef = useRef(windows);
  useEffect(() => { windowsRef.current = windows; }, [windows]);

  /**
   * The progress channel handed to `produce`. Group reports run through the
   * async job path and take minutes, so "Generating..." with no number is
   * indistinguishable from a hang; the producer publishes
   * {members_done, members_total} through here and the popup renders it.
   *
   * Bound to a window id here rather than passed in by the caller because the
   * producer is created before the window exists and never learns its id.
   */
  const progressFor = useCallback((id) => (progress) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, progress: progress || null } : w)));
  }, []);

  const openReportWindow = useCallback(({ reportName, produce, onCancel }) => {
    const handle = openReportPopup({ title: reportName || 'Report' });
    if (!handle) return Promise.reject(new Error('POPUP_BLOCKED'));
    const id = nextId();
    handles.current.set(id, handle);
    if (typeof onCancel === 'function') cancels.current.set(id, onCancel);
    setWindows((prev) => [
      ...prev,
      { id, status: 'loading', reportType: null, reportName: reportName || 'Report', reportData: null, requestParams: null, error: null, progress: null },
    ]);
    return Promise.resolve()
      .then(() => produce({ onProgress: progressFor(id) }))
      .then((payload) => {
        cancels.current.delete(id); // produce finished - close is no longer a cancel
        setWindows((prev) => prev.map((w) => (w.id === id
          ? { ...w, status: 'ready', reportType: payload.reportType, reportName: payload.reportName || w.reportName, reportData: payload.reportData, requestParams: payload.requestParams || null, progress: null }
          : w)));
        return payload;
      })
      .catch((err) => {
        cancels.current.delete(id);
        setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, status: 'error', error: (err && err.message) || 'Failed to generate report', progress: null } : w)));
        throw err;
      });
  }, [progressFor]);

  /**
   * Regenerate an OPEN window's report in place - the "rerun with different
   * parameters" path (windows/ReportPopup.jsx). Mirrors openReportWindow minus
   * the window.open: a click inside a popup grants user activation to the popup,
   * not the opener, so a rerun cannot reliably open a new window anyway.
   *
   * A failed rerun RESTORES the report that was on screen and rethrows, rather
   * than dropping the window into the 'error' state - that state is for a window
   * that never had a report, and a bad date should not cost the PM the report
   * they were reading.
   */
  const rerunReportWindow = useCallback((id, { produce, onCancel }) => {
    const prev = windowsRef.current.find((w) => w.id === id);
    if (!prev) return Promise.reject(new Error('NO_SUCH_WINDOW'));
    // Only one rerun may own this window at a time: the snapshot captured
    // below and the single cancel hook in `cancels` both assume exclusive
    // occupancy, so a second overlapping call (rerun-on-rerun, or a rerun
    // racing the window's very first load) would clobber the first one's
    // restore snapshot and cancel hook instead of rejecting cleanly.
    if (prev.status === 'loading') return Promise.reject(new Error('RERUN_IN_FLIGHT'));
    if (typeof onCancel === 'function') cancels.current.set(id, onCancel);
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, status: 'loading', error: null, progress: null } : w)));
    return Promise.resolve()
      .then(() => produce({ onProgress: progressFor(id) }))
      .then((payload) => {
        cancels.current.delete(id);
        setWindows((ws) => ws.map((w) => (w.id === id
          ? { ...w, status: 'ready', reportType: payload.reportType, reportName: payload.reportName || w.reportName, reportData: payload.reportData, requestParams: payload.requestParams || null, progress: null }
          : w)));
        return payload;
      })
      .catch((err) => {
        cancels.current.delete(id);
        // Restoring the snapshot also drops the failed rerun's progress counter,
        // which must not linger beside the report it never replaced.
        setWindows((ws) => ws.map((w) => (w.id === id ? { ...prev } : w)));
        throw err;
      });
  }, [progressFor]);

  const closeReportWindow = useCallback((id) => {
    if (statusOf(id) === 'loading') {
      const onCancel = cancels.current.get(id);
      if (onCancel) { try { onCancel(); } catch (e) { /* ignore */ } }
    }
    cancels.current.delete(id);
    const handle = handles.current.get(id);
    if (handle && !handle.closed) { try { handle.close(); } catch (e) { /* ignore */ } }
    handles.current.delete(id);
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, [statusOf]);

  const getHandle = useCallback((id) => handles.current.get(id) || null, []);

  // Close child popups when the opener tab unloads, so no blank zombie windows are
  // left behind (report popups are portals driven by this tab).
  useEffect(() => {
    const onUnload = () => {
      handles.current.forEach((h) => { if (h && !h.closed) { try { h.close(); } catch (e) { /* ignore */ } } });
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  return (
    <ReportWindowsContext.Provider value={{ windows, openReportWindow, rerunReportWindow, closeReportWindow, getHandle }}>
      {children}
    </ReportWindowsContext.Provider>
  );
};

export const useReportWindows = () => {
  const ctx = useContext(ReportWindowsContext);
  if (!ctx) throw new Error('useReportWindows must be used within ReportWindowsProvider');
  return ctx;
};
